// Current Action Review (approved HTML: 01-Current-Action-Review.html,
// SHA-256 0446f64cb7f30d79d0bf6904b49d63f81d599795370a1a1e78eb951b441a6448).
//
// Top-level: priority focus card, identity row, customer tabs,
// conversation card (with CS ticket shortcut), horse / order right stack,
// next customer preview, Finish and Next footer.
//
// R9 companion-state wiring: the primary data-fetching region
// (PriorityFocus + ConversationCard + RightStack) is wrapped in
// DataBoundary so loading, empty, error, degraded, blocked, and
// mid-call states render through the shared tokens instead of the
// default illustrative mock. The Default state visual is unchanged.
// External signals arrive via the optional `companionSignals` prop so
// the runtime can inject real priority-engine / Zoho-gate / Kixie
// call-evidence outcomes without this file branching on their shapes.
//
// W25 (2026-10-20) real-contact wiring: when a session is active (not
// preview-fixture / dev-mock), the identity row + breadcrumb render
// against the contact profile fetched from GET /v1/contacts/:id.
//
// W26 (2026-10-20) HARD RULE: on a real session NO fixture string may
// render (no Sarah / Mitchell / Duke / Jamie Dorsey / Michael / leaking
// bottle). The SARAH / MICHAEL / DUKE records and CURRENT_ACTION_MOCK
// are reachable ONLY when useContactProfile reports status 'skipped'
// (explicit ?preview= or the dev mock session). Every other status,
// including loading / error / empty, renders real data or an honest
// placeholder. See __tests__/real-contact-no-fixture.test.tsx.
//
// W27 (2026-10-20) Finish & Next walks REAL records: the footer button
// posts /v1/reps/:repId/queue/advance {outcome}, moves the url-bootstrap
// session to the delivered contact (so X-Customer-Id follows) and
// navigates to /current-action/:customerId. "Why this record" renders
// the queue item's plain-English reasons; "Up next" shows
// /queue/lookahead?n=3. See __tests__/finish-next-real-queue.test.tsx.
//
// W40.1 (2026-10-21) majors M1, M2, M5 on a real session:
//   M1 the approved file-shortcuts row (FileShortcuts) sits between the
//      identity row and the tabs, navigation only;
//   M2 the approved "Up next" card (QueueNextCustomer) is hydrated from
//      the W27 lookahead instead of the W27 list strip, same data, same
//      honest states; Finish & Next is unchanged;
//   M5 RecentActivityCard shows Create CS Ticket / Send Reply inert with
//      the live-write gate copy and the read-only Expand thread history.
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { PriorityFocus, type PriorityOwner } from '../_shared/PriorityFocus.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { FileShortcuts } from '../_shared/FileShortcuts.js';
import { NextCustomer, QueueNextCustomer } from '../_shared/NextCustomer.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import {
  resolveCompanionState,
  type ScreenCompanionSignals,
} from '../_shared/useScreenCompanionState.js';
import { useActiveCallSignal, mergeMidCallSignal } from '../_shared/useActiveCallSignal.js';
import { useContactProfile, type ContactProfileRecord } from '../_shared/useContactProfile.js';
import { useContactHorses, useContactTimeline } from '../_shared/useContactCollections.js';
import { useRep } from '../_shared/useRep.js';
import { QUEUE_OUTCOMES, useRepQueue, type QueueOutcome } from '../_shared/useRepQueue.js';
import { ConversationCard } from './components/ConversationCard.js';
import { CsTicketDialog } from './components/CsTicketDialog.js';
import { RecentActivityCard } from './components/RecentActivityCard.js';
import { RightStack } from './components/RightStack.js';
import { WhyThisRecord } from './components/QueuePanels.js';
import { CURRENT_ACTION_MOCK } from './mockData.js';
import { MICHAEL, SARAH, type MockCustomer } from '../../data/mockData.js';
import { useOptionalSession } from '../../auth/session-context.js';
import './styles.css';
import './app.css';

export interface CurrentActionReviewScreenProps {
  /** Injected test seam for the six companion states. */
  companionSignals?: ScreenCompanionSignals;
}

const RECENT_CALL_WINDOW_DAYS = 30;

function titleCase(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function initialsFor(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  const out = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
  return out || '??';
}

function monthYear(iso: string | null): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysSince(dateText: string | null, now: number): number | null {
  if (!dateText) return null;
  const ms = Date.parse(dateText);
  if (!Number.isFinite(ms)) return null;
  return Math.floor((now - ms) / 86_400_000);
}

export interface RealHero {
  heading: string;
  whatHeading: string;
  whatBody: string | undefined;
  whyNow: string;
  nextAction: string;
  nextActionDetail: string | undefined;
  owner: PriorityOwner;
  ownerDetail: string;
}

/**
 * Derive the priority hero from the real HubSpot profile. No engine,
 * no fixture: every string is either a field from the contact or a
 * fixed instruction. Exported for the unit test.
 */
export function deriveRealHero(contact: ContactProfileRecord, now: number = Date.now()): RealHero {
  const firstName = contact.firstName || contact.fullName.split(' ')[0] || 'this customer';
  const age = daysSince(contact.lastCallDate, now);
  const recent = age !== null && age >= 0 && age < RECENT_CALL_WINDOW_DAYS;
  const status = contact.leadStatusVs || (contact.lifecycleStage ? titleCase(contact.lifecycleStage) : null);
  const comment = contact.lastCommentVs && contact.lastCommentVs.trim().length > 0
    ? contact.lastCommentVs.trim()
    : null;
  return {
    heading: recent ? `Follow up with ${firstName}` : `Call ${firstName}`,
    whatHeading: status ?? 'Check in on their horse and current supply',
    whatBody: status ? 'Check in on their horse and current supply' : undefined,
    whyNow: contact.lastCallDate
      ? `Last call ${contact.lastCallDate}: ${comment ?? 'no comment logged'}`
      : 'No call logged yet in VanillaSoft',
    nextAction: "Confirm what they're feeding today and log the call",
    nextActionDetail: contact.callPriorityVs ? `VanillaSoft priority: ${contact.callPriorityVs}` : undefined,
    owner: contact.ownerName
      ? { initials: initialsFor(contact.ownerName), fullName: contact.ownerName }
      : { initials: 'UN', fullName: 'Unassigned, you take it' },
    ownerDetail: contact.ownerName
      ? 'HubSpot contact owner.'
      : 'No HubSpot owner on this contact yet.',
  };
}

export function CurrentActionReviewScreen(
  props: CurrentActionReviewScreenProps = {},
): ReactNode {
  const params = useParams();
  const navigate = useNavigate();
  const customerId = params.customerId || SARAH.id;
  const [ticketCreated, setTicketCreated] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeChannelLabel] = useState('Current text conversation attached');

  // W25: fetch the real HubSpot contact profile for the current route.
  // In preview / dev-mock mode the hook returns status='skipped' and
  // the screen falls back to the SARAH illustrative record, which keeps
  // the design-review build visually identical.
  const contactProfile = useContactProfile(customerId);
  // W26: preview is ONLY status 'skipped'. loading / error / empty are
  // real-session states and must never show the fixture.
  const isPreview = contactProfile.status === 'skipped';
  const timeline = useContactTimeline(customerId);
  const horses = useContactHorses(customerId);
  const repState = useRep();
  const repFirstName =
    repState.status === 'ready' ? repState.rep.fullName.split(/\s+/)[0] || null : null;

  const activeCustomer = useMemo<MockCustomer>(() => {
    if (isPreview) return SARAH;
    const c = contactProfile.contact;
    if (contactProfile.status === 'ready' && c) {
      return {
        id: c.id,
        initials: c.initials,
        firstName: c.firstName || c.fullName.split(' ')[0] || c.id,
        fullName: c.fullName,
        customerSince: monthYear(c.createdAt) ?? 'date unknown',
        city: c.city || '',
        stateCode: c.state || '',
        localTime: '',
        phone: c.phone || '',
        email: c.email || '',
        // W38: real HubSpot `preferred_channel`; '' renders as "not set".
        preferredChannel: typeof c.preferredChannel === 'string' ? c.preferredChannel.trim() : '',
      };
    }
    // Real session, profile not ready: honest placeholder, never SARAH.
    const label =
      contactProfile.status === 'loading'
        ? 'Loading contact…'
        : contactProfile.status === 'empty'
          ? `No HubSpot contact ${customerId}`
          : 'Contact could not load';
    return {
      id: customerId,
      initials: customerId.slice(0, 2).toUpperCase(),
      firstName: '',
      fullName: label,
      customerSince: 'date unknown',
      city: '',
      stateCode: '',
      localTime: '',
      phone: '',
      email: '',
      preferredChannel: '',
    };
  }, [isPreview, contactProfile.status, contactProfile.contact, customerId]);

  const realHero = useMemo<RealHero | null>(() => {
    if (isPreview || contactProfile.status !== 'ready' || !contactProfile.contact) return null;
    return deriveRealHero(contactProfile.contact);
  }, [isPreview, contactProfile.status, contactProfile.contact]);

  const primaryHorseName = useMemo<string | null>(() => {
    const named = horses.items.find((h) => typeof h.name === 'string' && h.name.trim().length > 0);
    return named && typeof named.name === 'string' ? named.name.trim() : null;
  }, [horses.items]);

  function handleTicketConfirmed(_issueSummary: string): void {
    // TODO(runtime): submit ZOHO_CREATE_TICKET and record the audit
    // ledger entry per feedback_confirmation_popup_pattern.
    setTicketId(CURRENT_ACTION_MOCK.ticketIdExample);
    setTicketCreated(true);
    setDialogOpen(false);
  }

  // W27: the rep queue behind Finish & Next. Preview / dev-mock sessions
  // report status 'skipped' and render none of the queue UI.
  const sessionCtx = useOptionalSession();
  const queue = useRepQueue(customerId);
  const queueMatchesRoute = !!queue.current && queue.current.customerId === customerId;
  const [outcome, setOutcome] = useState<QueueOutcome>('reached');
  const [finishNote, setFinishNote] = useState<string | null>(null);
  useEffect(() => {
    // A new record on screen starts with a clean footer note.
    setFinishNote(null);
  }, [customerId]);

  // W27: the fixture-bound "Confirm the prepared ticket first" gate is
  // gone. The button only waits while an advance is in flight.
  const finishDisabled = queue.advancing;
  const noteWhenDisabled = 'Loading the next record…';
  const noteWhenEnabled = isPreview
    ? ticketCreated
      ? 'Ticket recorded · Ready for next customer'
      : 'Ready for next customer'
    : finishNote ??
      (queueMatchesRoute
        ? 'Logs the outcome and opens the next queued customer'
        : 'Starts the queue and opens the next customer');

  async function handleFinishNext(): Promise<void> {
    if (isPreview) return;
    setFinishNote(null);
    const result = await queue.advance(outcome);
    switch (result.kind) {
      case 'advanced': {
        const next = result.current;
        // Move the session first so the contact reads for the new route
        // carry X-Customer-Id = the delivered record, then navigate.
        sessionCtx?.setCustomerId(next.customerId, next.workId ? { workId: next.workId } : null);
        navigate(`/current-action/${encodeURIComponent(next.customerId)}`);
        return;
      }
      case 'clear':
        setFinishNote('Queue is clear: every eligible customer has been served this session.');
        return;
      case 'degraded':
        setFinishNote(`Queue lookup failed (${result.errorCode}). Try again.`);
        return;
      case 'error':
        setFinishNote(`Could not advance (${result.errorCode}).`);
        return;
      default:
        return;
    }
  }

  // Companion state. Internal signals today are limited to the ticket
  // dialog lifecycle; the richer sources (priority-engine fetch state,
  // ZOHO gate) arrive via `companionSignals`. R10: the Kixie mid-call
  // signal arrives via the active-call SSE stream through
  // useActiveCallSignal; the prop-based signal still wins so unit tests
  // remain hermetic.
  const liveMidCall = useActiveCallSignal({
    customerId,
    label: `Call in progress with ${activeCustomer.firstName || activeCustomer.fullName}`,
    actionName: 'KIXIE_CALL_IN_PROGRESS',
  });

  const mergedSignals = useMemo(
    () => mergeMidCallSignal(props.companionSignals, liveMidCall),
    [props.companionSignals, liveMidCall],
  );
  // W27: the queue no longer drives the DataBoundary. Before, an empty
  // queue (`current` null, which is every rep before the first Finish &
  // Next) replaced the whole real screen with "No prioritized action";
  // the record the rep opened must stay on screen regardless.
  const state = useMemo<BoundaryState>(
    () => resolveCompanionState(mergedSignals),
    [mergedSignals],
  );

  // Identity badge: real lifecycle stage on a real session, the approved
  // design copy only in preview.
  const badgeLabel = isPreview
    ? 'Customer replied · Just now'
    : contactProfile.contact?.lifecycleStage
      ? titleCase(contactProfile.contact.lifecycleStage)
      : 'Contact';
  const sinceLabel = isPreview
    ? undefined
    : contactProfile.contact?.lastCallDate
      ? `Last VanillaSoft call ${contactProfile.contact.lastCallDate}`
      : contactProfile.contact?.createdAt
        ? `In HubSpot since ${monthYear(contactProfile.contact.createdAt)}`
        : ' ';

  const previewContent: ReactNode = (
    <>
      <PriorityFocus
        ariaLabel="Highest priority right now"
        eyebrow={CURRENT_ACTION_MOCK.priorityEyebrow}
        heading={CURRENT_ACTION_MOCK.priorityHeading}
        what={{ label: 'WHAT', heading: CURRENT_ACTION_MOCK.what, iconId: 'message' }}
        whyNow={{ label: 'WHY NOW', body: CURRENT_ACTION_MOCK.whyNow, iconId: 'clock' }}
        nextAction={{
          label: 'NEXT ACTION',
          body: CURRENT_ACTION_MOCK.nextAction,
          detail: CURRENT_ACTION_MOCK.nextActionDetail,
          iconId: 'target',
        }}
        ownership={{ label: 'OWNERSHIP', iconId: 'people' }}
      />
      <IdentityRow customer={activeCustomer} badgeLabel={badgeLabel} />
      <FileShortcuts customerId={customerId} firstName={activeCustomer.firstName} />
      <CustomerTabs customerId={customerId} active="current" />
      <div className="content-grid">
        <ConversationCard
          ticketCreated={ticketCreated}
          ticketId={ticketId}
          onOpenCsTicket={() => setDialogOpen(true)}
        />
        <RightStack />
      </div>
    </>
  );

  const realContent: ReactNode = (
    <>
      {realHero ? (
        <PriorityFocus
          ariaLabel="Highest priority right now"
          eyebrow="Highest priority right now"
          heading={realHero.heading}
          what={{ label: 'WHAT', heading: realHero.whatHeading, body: realHero.whatBody, iconId: 'message' }}
          whyNow={{ label: 'WHY NOW', body: realHero.whyNow, iconId: 'clock' }}
          nextAction={{
            label: 'NEXT ACTION',
            body: realHero.nextAction,
            detail: realHero.nextActionDetail,
            iconId: 'target',
          }}
          ownership={{ label: 'OWNERSHIP', iconId: 'people', detail: realHero.ownerDetail }}
          owner={realHero.owner}
        />
      ) : (
        <PriorityFocus
          ariaLabel="Highest priority right now"
          eyebrow="Highest priority right now"
          heading={
            contactProfile.status === 'loading'
              ? 'Loading contact…'
              : contactProfile.status === 'empty'
                ? 'No HubSpot contact for this id'
                : `Contact could not load (${contactProfile.errorCode || 'ERR'})`
          }
          what={{ label: 'WHAT', heading: 'Waiting on HubSpot', iconId: 'message' }}
          whyNow={{ label: 'WHY NOW', body: 'The contact record has not loaded yet.', iconId: 'clock' }}
          nextAction={{ label: 'NEXT ACTION', body: 'Wait for the record, then call.', iconId: 'target' }}
          ownership={{ label: 'OWNERSHIP', iconId: 'people', detail: 'Owner loads with the contact.' }}
          owner={{ initials: '..', fullName: 'Loading owner' }}
        />
      )}
      <WhyThisRecord
        status={queue.status}
        matchesRoute={queueMatchesRoute}
        current={queue.current}
        errorCode={queue.errorCode}
      />
      <IdentityRow customer={activeCustomer} badgeLabel={badgeLabel} sinceLabel={sinceLabel} />
      <FileShortcuts
        customerId={customerId}
        firstName={contactProfile.status === 'ready' ? activeCustomer.firstName : ''}
      />
      <CustomerTabs customerId={customerId} active="current" />
      <div className="content-grid">
        <RecentActivityCard
          customerFirstName={contactProfile.status === 'ready' ? activeCustomer.firstName : ''}
          repFirstName={repFirstName}
          horseName={primaryHorseName}
          timeline={timeline}
          liveWritesPaused={!isPreview}
        />
        <RightStack
          mode="real"
          horses={horses}
          onViewHorses={() => navigate(`/horses/${encodeURIComponent(customerId)}`)}
        />
      </div>
    </>
  );

  return (
    <WorkspaceChrome screen="current-action" rail="my-work">
      <div className="breadcrumbs">
        <div>
          My work
          <span className="slash">/</span>
          <strong>{activeCustomer.fullName}</strong>
        </div>
        {contactProfile.status === 'skipped' ? (
          <span className="preview">Design preview · Sample customer</span>
        ) : contactProfile.status === 'loading' ? (
          <span className="preview">Loading contact…</span>
        ) : contactProfile.status === 'error' ? (
          <span className="preview">Contact load failed ({contactProfile.errorCode || 'ERR'})</span>
        ) : contactProfile.status === 'empty' ? (
          <span className="preview">No HubSpot contact for id {customerId}</span>
        ) : null}
      </div>
      <DataBoundary state={state} testId="current-action-boundary">
        {isPreview ? previewContent : realContent}
      </DataBoundary>
      {isPreview ? (
        <CsTicketDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleTicketConfirmed}
          attachedChannelLabel={activeChannelLabel}
        />
      ) : null}
      {isPreview ? (
        <NextCustomer
          customer={MICHAEL}
          summary="Help plan his next subscription order"
          trigger="Customer emailed"
          age="8 minutes ago"
        />
      ) : (
        <QueueNextCustomer
          status={queue.lookaheadStatus}
          items={queue.lookahead}
          degraded={queue.lookaheadDegraded}
        />
      )}
      <ActionFooter
        disabled={finishDisabled}
        noteWhenDisabled={noteWhenDisabled}
        noteWhenEnabled={noteWhenEnabled}
        summaryBody={
          isPreview
            ? ticketCreated
              ? 'Customer Service handles the issue. The system tracks follow-through.'
              : 'Your next step is retained with this customer.'
            : 'The outcome is kept with the queue position; no HubSpot write and no dial fires yet.'
        }
        controls={
          isPreview ? undefined : (
            <label className="outcome-pick">
              Outcome
              <select
                aria-label="Call outcome"
                value={outcome}
                disabled={queue.advancing}
                onChange={(e) => setOutcome(e.target.value as QueueOutcome)}
              >
                {QUEUE_OUTCOMES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          )
        }
        onFinish={() => {
          void handleFinishNext();
        }}
      />
    </WorkspaceChrome>
  );
}

export default CurrentActionReviewScreen;
